import { EDNVal, toEDNString, toEDNStringFromSimpleObject } from "edn-data";
import { queryOmegaSingleResult } from "../../api";
import {
  FolderPropertyTypesMap,
  GetFolderPropertyTypesResponse,
  GetPropertyValuesResponse,
  PropertyType,
  PropertyTypesMap,
  PropertyValue,
} from "../../types/page/property";

// Typescript wrapper for https://github.com/The-Lambda-Group/query-omega-oql/blob/development/oql/app/public/api/page/property.oql

/**
 * Add a property type to a database
 * @param pageId the database of which to add the property to
 * @param appId workspace ID
 * @param data any class implementing the PropertyTypeData interface
 */
export const addProperty: (config: {
  data: PropertyType;
}) => Promise<any> = async ({ data }) => {
  const ednObject = {
    data,
  } as any;
  const ednString = toEDNStringFromSimpleObject(ednObject, {
    keysAs: "string",
  });
  const query = `
        (datastore Qo.Public.Api.Page.Prop "omega/query-omega/public/api/page/property")
        (Qo.Public.Api.Page.Prop/add-property ${ednString} Response)
        (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

/**
 * Get all the property types in a database
 * @param pageId the database ID to list property types of
 * @param appId workspace ID
 */
export const getFolderPropertyTypes: (config: {
  folderId: string;
}) => Promise<GetFolderPropertyTypesResponse> = async ({ folderId }) => {
  const ednString = toEDNString({
    map: [["folder-id", folderId]],
  });
  const query = `
        (datastore Qo.Public.Api.Page.Prop "omega/query-omega/public/api/page/property")
        (Qo.Public.Api.Page.Prop/get-properties ${ednString} Response)
        (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const getFolderPrimaryKey: (config: {
  "folder-id": string;
}) => Promise<string[]> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config as any, {
    keysAs: "string",
  });
  const query = `
        (datastore Qo.Public.Api.Page.Prop "omega/query-omega/public/api/page/property")
        (Qo.Public.Api.Page.Prop/folder-primary-key ${ednString} Response)
        (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const writeFolderPrimaryKey: (config: {
  "folder-id": string;
  "primary-key": string[];
}) => Promise<string[]> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config as any, {
    keysAs: "string",
  });
  const query = `
        (datastore Qo.Public.Api.Page.Prop "omega/query-omega/public/api/page/property")
        (Qo.Public.Api.Page.Prop/write-folder-primary-key ${ednString} Response)
        (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const writePropertyTypes: (config: {
  properties: PropertyTypesMap;
  "folder-id"?: string;
}) => Promise<void> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config as any, {
    keysAs: "string",
  });
  const query = `
        (datastore Qo.Public.Api.Page.Prop "omega/query-omega/public/api/page/property")
        (Qo.Public.Api.Page.Prop/set-folder-properties ${ednString} Response)
        (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

/**
 * Get all the values of a specific property type in a database
 * @param propId the property ID of the property type you want to list values for
 * @param appId workspace ID
 */
export const getPropertyValues: (config: {
  "app-id": string;
  "prop-id"?: string;
  "page-id"?: string;
  "folder-id"?: string;
  "page-ids"?: string[];
}) => Promise<GetPropertyValuesResponse> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config, { keysAs: "string" });
  const query = `
        (datastore Qo.Public.Api.Page.Prop "omega/query-omega/public/api/page/property")
        (Qo.Public.Api.Page.Prop/get-property-values ${ednString} Response)
        (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

/**
 * Get all the values of a specific property type in a database
 * @param propId the property ID of the property type you want to list values for
 * @param appId workspace ID
 */
export const getFolderSecIndexes: (config: {
  "folder-id": string;
}) => Promise<string[]> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config as any, {
    keysAs: "string",
  });
  const query = `
        (datastore Qo.Public.Api.Page.Prop "omega/query-omega/public/api/page/property")
        (Qo.Public.Api.Page.Prop/get-folder-sec-indexes ${ednString} Response)
        (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const writeFolderSecIndexes: (config: {
  "folder-id": string;
  "sec-indexes": string[];
}) => Promise<string[]> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config as any, {
    keysAs: "string",
  });
  const query = `
        (datastore Qo.Public.Api.Page.Prop "omega/query-omega/public/api/page/property")
        (Qo.Public.Api.Page.Prop/write-folder-sec-indexes ${ednString} Response)
        (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const renameProperty: (config: {
  "folder-id": string;
  "old-name": string;
  "new-name": string;
}) => Promise<unknown> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config as any, { keysAs: "string" });
  const query = `
        (datastore Qo.Public.Api.Page.Prop "omega/query-omega/public/api/page/property")
        (Qo.Public.Api.Page.Prop/rename-property ${ednString} Response)
        (return [Response])
    `;
  return queryOmegaSingleResult(query);
};

export const setPropertyValue: (config: {
  "prop-val": PropertyValue;
  "page-id": string;
  "prop-name": string;
}) => Promise<any> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config as any, {
    keysAs: "string",
  });
  const query = `
        (datastore Qo.Public.Api.Page.Prop "omega/query-omega/public/api/page/property")
        (Qo.Public.Api.Page.Prop/set-property-value ${ednString} Response)
        (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};
